import React from 'react';
import { Settings } from 'lucide-react';
import { useLocation } from 'wouter';
import {
  ControlledSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/ControlledSelect';
import { Button } from '@/components/ui/button';
import { useContextRouting } from '@/hooks/useContextRouting';
import { useUserContexts, useActiveContext } from '@/hooks/useActiveContext';
import { Id } from '@/convex/_generated/dataModel';
import { ContextIcon } from '@/lib/context-icons';

export function ContextSwitcher() {
  const { navigateToContext, currentContextId, currentPath } = useContextRouting();
  const userContexts = useUserContexts();
  const { activeContext } = useActiveContext();
  const [, navigate] = useLocation();
  
  const handleContextSwitch = (contextId: string) => {
    // Maintain current page when switching contexts
    navigateToContext(contextId as Id<'contexts'>, currentPath);
  };
  
  if (!userContexts || userContexts.length === 0) {
    return (
      <div className="p-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/contexts')}
          className="w-full justify-start"
        >
          <Settings className="h-4 w-4 mr-2" />
          Manage Contexts
        </Button>
      </div>
    );
  }
  
  const currentContext = userContexts.find(c => c._id === currentContextId) || activeContext;
  
  // Ensure we always have a value for controlled component
  const selectValue = currentContextId || '__placeholder__';

  return (
    <div className="p-2">
      <Select 
        value={selectValue} 
        onValueChange={(value) => {
          // Prevent the select from updating when manage is selected
          if (value === '__placeholder__') {
            // Ignore placeholder selection
            return;
          } else if (value !== '__manage__') {
            handleContextSwitch(value);
          } else {
            navigate('/contexts');
          }
        }}
      >
        <SelectTrigger className="w-full">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ContextIcon icon={currentContext?.icon} className="w-4 h-4" />
            <SelectValue className="truncate">
              {currentContext?.name || 'Select Context'}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {userContexts.map((context) => (
            <SelectItem key={context._id} value={context._id}>
              <div className="flex items-center gap-2">
                <ContextIcon icon={context.icon} className="w-4 h-4" />
                <span className="truncate">{context.name}</span>
                {context.userRole && (
                  <span className="text-xs text-muted-foreground">
                    ({context.userRole})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
          <div className="border-t border-border mt-1 pt-1">
            <SelectItem value="__manage__">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Manage Contexts</span>
              </div>
            </SelectItem>
          </div>
        </SelectContent>
      </Select>
    </div>
  );
}